import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";
import { Bar, Radar } from "react-chartjs-2";
import { baseUrl } from "../app/api/apiSlice";
import {
  Container,
  Header,
  Grid,
  Icon,
  Loader,
  Tab,
} from "semantic-ui-react";
import CustomDropdown from "./CustomDropdown";
import ProductKnowledgeByProduct from "./ProductKnowledgeByProduct";
import { ListTable, RatioScore, Tag, scoreColor } from "./listPage";
import { radarDataset, radarLabel, radarOptions as sharedRadarOptions } from "./chartStyle";
import { PAGE_SIZE } from "../constants";
import "./LandingPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
);

export const RANGE_OPTIONS = [
  { key: "1d", text: "1일", value: "1d" },
  { key: "1w", text: "1주", value: "1w" },
  { key: "1m", text: "1개월", value: "1m" },
  // The graded quota's period, and 직원 관리's default 평가 완료 window: the same numbers.
  { key: "q", text: "이번 분기", value: "q" },
  { key: "6m", text: "6개월", value: "6m" },
];

const VALID_RANGES = RANGE_OPTIONS.map((o) => o.value);

const RANGE_DAYS = { "1d": 1, "1w": 7, "1m": 30, "6m": 180 };
/** First day of the quarter in KST (UTC+9), as the API's 이번 분기 and 직원 관리 count it. */
export const quarterStartDate = (now = new Date()) => {
  const kst = new Date(now.getTime() + 9 * 3600000);
  return `${kst.getUTCFullYear()}-${String(Math.floor(kst.getUTCMonth() / 3) * 3 + 1).padStart(2, "0")}-01`;
};
/** The first day the dashboard's range covers, for links into the lists (YYYY-MM-DD). */
export const rangeStartDate = (range) =>
  range === "q"
    ? quarterStartDate()
    : new Date(Date.now() - (RANGE_DAYS[range] || 30) * 86400000).toISOString().slice(0, 10);

export function resolveInitialRange(search) {
  const value = new URLSearchParams(search).get("range");
  // 이번 분기 by default, as 직원 관리: the graded quota's period, so the two pages agree.
  return VALID_RANGES.includes(value) ? value : "q";
}

const StaffHome = ({ user, navigate }) => {
  const token = useSelector((state) => state.auth.token);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Contractor Filter State (staff only)
  const [contractors, setContractors] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState(null);

  const [range, setRange] = useState(() =>
    resolveInitialRange(window.location.search)
  );

  // 700, not the app's usual 768: this breakpoint is measured, not inherited. The title
  // block is ~165px, the pills ~206px and the dropdown 180px, so one row needs ~575px of
  // container and fits down to roughly a 610px viewport. Stacking at 768 stretched the
  // segmented control to 723px on a tablet that had room to spare. 700 leaves headroom
  // for a longer 이름/파트너 widening the title, and the outer flexWrap below is the
  // backstop if one ever does.
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 700);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleRangeChange = (value) => {
    setRange(value);
    const params = new URLSearchParams(window.location.search);
    params.set("range", value);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`
    );
  };

  useEffect(() => {
    if (!user.is_staff) return;
    axios
      .get(`${baseUrl}/api/v1/accounts/contractors/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const results = res.data.results || res.data;
        const formatted = (Array.isArray(results) ? results : []).map((c) => ({
          key: c.id,
          text: c.name,
          value: c.id,
        }));
        setContractors(formatted);
      })
      .catch((err) => console.error("Failed to fetch contractors", err));
  }, [token, user.is_staff]);

  useEffect(() => {
    // Aborted when range/파트너 change again (or StrictMode's dev re-run), so a slower,
    // older response can't land after a newer one.
    const controller = new AbortController();
    const fetchStats = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("range", range);
        if (selectedContractor) {
          params.set("contractor_id", selectedContractor);
        }
        const url = `${baseUrl}/api/v1/dashboard/stats/?${params.toString()}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        setStats(response.data);
        setLoading(false);
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("Failed to fetch dashboard stats", error);
        setLoading(false);
      }
    };

    fetchStats();
    return () => controller.abort();
  }, [token, selectedContractor, range]);

  // --- CHART OPTIONS ---
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.8)",
        padding: 10,
        cornerRadius: 8,
        titleFont: {
          family: "-apple-system, BlinkMacSystemFont, sans-serif",
          size: 13,
        },
        bodyFont: {
          family: "-apple-system, BlinkMacSystemFont, sans-serif",
          size: 12,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: "-apple-system, BlinkMacSystemFont, sans-serif" },
        },
      },
      y: {
        border: { display: false },
        grid: { color: "#f0f0f0" },
        beginAtZero: true,
        ticks: {
          font: { family: "-apple-system, BlinkMacSystemFont, sans-serif" },
        },
      },
    },
  };


  const SummarySection = () => (
    <Grid stackable stretched columns={4} style={{ marginBottom: "20px" }}>
      {[
        {
          label: "총 교육생",
          value: stats.counts.trainees,
          icon: "users",
          color: "#007AFF",
        },
        {
          label: "총 응대 세션",
          value: stats.counts.sessions,
          sub: stats.counts.sessions_practice != null
            ? `평가 ${stats.counts.sessions - stats.counts.sessions_practice} · 연습 ${stats.counts.sessions_practice}`
            : null,
          icon: "comments",
          color: "#5856D6",
        },
        {
          label: "총 퀴즈 풀이",
          value: stats.counts.quiz_attempts,
          icon: "pencil alternate",
          color: "#FF9500",
        },
        {
          label: "참여 파트너",
          // Partners with at least one trainee active in the period (파트너 현황 lists them all).
          value: stats.counts.active_partners ?? stats.contractor_stats.length,
          icon: "building",
          color: "#8E8E93",
        },
      ].map((item, idx) => (
        <Grid.Column key={idx}>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%", // 총 응대 세션 has a second line; the row stays one height
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#86868b",
                  fontWeight: "500",
                  marginBottom: "4px",
                }}
              >
                {item.label}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#1d1d1f",
                }}
              >
                {item.value}
              </div>
              {item.sub && (
                <div style={{ fontSize: "12px", color: "#86868b", marginTop: "2px" }}>{item.sub}</div>
              )}
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: `${item.color}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                name={item.icon}
                style={{ fontSize: "20px", color: item.color, margin: 0 }}
              />
            </div>
          </div>
        </Grid.Column>
      ))}
    </Grid>
  );

  // 파트너 현황 cells. 참여 / 평가 완료 are out of the approved trainees.
  const funnelCell = (n, of) => (
    <span style={{ whiteSpace: "nowrap" }}>
      {n}
      {of > 0 && <span style={{ fontSize: "12px", color: "#86868b" }}> ({Math.round((n / of) * 100)}%)</span>}
    </span>
  );
  const approvedCell = (c) => (
    <span style={{ whiteSpace: "nowrap" }}>
      {c.approved}
      {c.approved < c.signed_up && <span style={{ fontSize: "12px", color: "#B25E00" }}> · 대기 {c.signed_up - c.approved}</span>}
    </span>
  );
  const achievementCell = (c) =>
    c.avg_score == null ? (
      <span style={{ color: "#86868b" }}>—</span>
    ) : (
      <span style={{ whiteSpace: "nowrap" }}>
        <span style={{ fontWeight: 600, color: scoreColor(c.avg_score) }}>{Math.round(c.avg_score)}%</span>
        <span style={{ fontSize: "12px", color: "#86868b" }}> {c.session_count}회</span>
        {c.small_sample && <span style={{ fontSize: "12px", color: "#86868b" }}> · 표본 적음</span>}
      </span>
    );

  const ResponseSkillsTab = () => (
    <div>
      {/* Response Skills KPI Row */}
      <Grid stackable columns={3} style={{ marginBottom: "20px", marginTop: "20px" }}>
        {[
          // 달성률 (score ÷ its own criteria set's maximum): Apple's and LGU's maxima differ.
          { label: "평균 달성률", value: `${stats.counts.average_score}%`, icon: "star", color: "#34C759" },
          {
            // Threshold comes from the API (PASS_SCORE_PERCENT) rather than being written
            // here. The old label said "80점+" while the backend compared raw points to 80
            // against a 95-point maximum, so it read as a percentage and was not one.
            label: `Pass 비율 (달성률 ${stats.counts.pass_threshold_percent ?? 80}%+)`,
            value: `${stats.counts.pass_rate}%`,
            icon: "check circle",
            color: "#007AFF",
          },
          { 
            label: "평균 기준 충족률", 
            // Pooled over every criterion by the API, not a mean of the radar's areas.
            value: `${stats.counts.criteria_met_rate ?? 0}%`, 
            icon: "chart pie", 
            color: "#5856D6" 
          },
        ].map((item, idx) => (
          <Grid.Column key={idx}>
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '24px', 
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontSize: '13px', color: '#86868b', fontWeight: '500', marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1d1d1f' }}>{item.value}</div>
              </div>
              <div style={{ 
                width: '42px', 
                height: '42px', 
                borderRadius: '10px', 
                backgroundColor: `${item.color}15`, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Icon name={item.icon} style={{ fontSize: '18px', color: item.color, margin: 0 }} />
              </div>
            </div>
          </Grid.Column>
        ))}
      </Grid>

      {/* Section 2: Main Trends */}
      <Header as="h3" style={{ margin: "30px 0 20px", color: "#1d1d1f" }}>
        성과 분석
      </Header>
      <Grid stackable columns={2}>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              세션 추이
            </Header>
            {/* Sessions as 평가 / 연습 stacked on one axis, and 평균 달성률 on its own chart
                below: one chart with two y-axes read as if the line and bars shared a scale. */}
            <div style={{ height: "190px" }}>
              <Bar
                data={{
                  labels: stats.charts.sessions_over_time.labels,
                  datasets: [
                    {
                      label: "평가",
                      data: stats.charts.sessions_over_time.data.map(
                        (n, i) => n - (stats.charts.sessions_over_time.practice?.[i] || 0)
                      ),
                      backgroundColor: "#007AFF",
                      borderRadius: 3,
                      barThickness: 10,
                      stack: "s",
                    },
                    {
                      label: "연습",
                      data: stats.charts.sessions_over_time.practice || [],
                      backgroundColor: "#B3D4FF",
                      borderRadius: 3,
                      barThickness: 10,
                      stack: "s",
                    },
                  ],
                }}
                options={{
                  ...commonOptions,
                  plugins: {
                    ...commonOptions.plugins,
                    legend: {
                      display: true,
                      position: "top",
                      align: "end",
                      labels: { usePointStyle: true, boxWidth: 6, font: { size: 11 } },
                    },
                  },
                  scales: {
                    x: { ...commonOptions.scales.x, stacked: true, ticks: { ...commonOptions.scales.x.ticks, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
                    y: { ...commonOptions.scales.y, stacked: true, title: { display: true, text: "세션 수", font: { size: 11 } } },
                  },
                }}
              />
            </div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#86868b", margin: "14px 0 4px" }}>평균 달성률 (평가 세션)</div>
            <div style={{ height: "110px" }}>
              <Bar
                data={{
                  labels: stats.charts.sessions_over_time.labels,
                  datasets: [
                    {
                      type: "line",
                      label: "평균 달성률 (%)",
                      data: stats.charts.sessions_over_time.avg_scores,
                      borderColor: "#34C759",
                      backgroundColor: "#34C759",
                      borderWidth: 2,
                      pointRadius: 2.5,
                      tension: 0.3,
                      spanGaps: true,
                    },
                  ],
                }}
                options={{
                  ...commonOptions,
                  scales: {
                    x: { ...commonOptions.scales.x, ticks: { display: false } },
                    y: { ...commonOptions.scales.y, min: 0, max: 100, ticks: { ...commonOptions.scales.y?.ticks, stepSize: 50, callback: (v) => `${v}%` } },
                  },
                }}
              />
            </div>
          </div>
        </Grid.Column>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              영역별 평균 달성률
            </Header>
            <div
              style={{
                height: "300px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {/* The trainee dashboard's radar: Apple's stage names with their Korean gloss
                  and value ("Discover / 니즈 파악 40%"), not the raw tag codes. */}
              <Radar
                data={{
                  labels: stats.charts.radar_chart.labels.map((tag, i) =>
                    radarLabel(tag, stats.charts.radar_chart.data[i])
                  ),
                  datasets: [radarDataset(stats.charts.radar_chart.data)],
                }}
                options={sharedRadarOptions()}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>

      {/* Section 3: Deep Dive */}
      <Grid stackable columns={2} style={{ marginTop: "10px" }}>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              점수 분포
            </Header>
            <div style={{ height: "250px" }}>
              <Bar
                data={{
                  labels: stats.charts.score_distribution.labels,
                  datasets: [
                    {
                      data: stats.charts.score_distribution.data,
                      backgroundColor: "#007AFF",
                      borderRadius: 4,
                      barPercentage: 0.6,
                    },
                  ],
                }}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    x: {
                      ...commonOptions.scales.x,
                      title: {
                        display: true,
                        text: "달성률 구간",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                    y: {
                      ...commonOptions.scales.y,
                      title: {
                        display: true,
                        text: "세션 수",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </Grid.Column>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              어려워하는 페르소나 (하위 5)
            </Header>
            <div style={{ height: "250px" }}>
              {/* An empty axis reads as broken. This happens for a contractor with no
                  evaluations yet, for a short date range, and on demo data — whose sessions
                  were all imported from Apple's ACE files and therefore use placeholder
                  (is_sentinel) personas, which are excluded because a trainee cannot pick
                  them and so "personas trainees find hard" cannot mean anything for them. */}
              {(stats.charts.persona_distribution.data || []).length === 0 ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#86868b",
                    fontSize: "13px",
                    textAlign: "center",
                    lineHeight: 1.6,
                  }}
                >
                  선택한 기간에 평가된 페르소나가 없습니다.
                </div>
              ) : (
              <Bar
                data={{
                  // The session count goes in the axis label rather than the tooltip: it is
                  // the whole reason this ranking is readable. A persona seen once used to
                  // sit beside one seen fifty times looking equally hard, and a count you
                  // have to hover to discover does not fix that.
                  labels: (stats.charts.persona_distribution.personas || []).length
                    ? stats.charts.persona_distribution.personas.map(
                        (p) => `${p.label} · ${p.count}회${p.small_sample ? " (표본 적음)" : ""}`
                      )
                    : stats.charts.persona_distribution.labels,
                  datasets: [
                    {
                      data: stats.charts.persona_distribution.data,
                      // Fewer than 3 sessions: ranked after the rest by the API, drawn grey.
                      backgroundColor: (stats.charts.persona_distribution.personas || []).map(
                        (p) => (p.small_sample ? "#C7C7CC" : "#FF3B30")
                      ),
                      borderRadius: 4,
                      barThickness: 16,
                    },
                  ],
                }}
                options={{
                  ...commonOptions,
                  indexAxis: "y",
                  // A bar opens that persona's sessions in 대화 내역.
                  onClick: (evt, els) => {
                    const p = stats.charts.persona_distribution.personas?.[els[0]?.index];
                    if (p) navigate(`/chatsessionslist?page=1&page_size=${PAGE_SIZE}&persona=${p.id}&start_date=${rangeStartDate(range)}`);
                  },
                  onHover: (evt, els) => {
                    if (evt.native?.target) evt.native.target.style.cursor = els.length ? "pointer" : "default";
                  },
                  plugins: {
                    ...commonOptions.plugins,
                    tooltip: {
                      ...commonOptions.plugins.tooltip,
                      callbacks: {
                        // Who the persona is, for a manager deciding what to coach. The bar
                        // itself only carries a number.
                        title: (items) => {
                          const p =
                            stats.charts.persona_distribution.personas?.[
                              items[0].dataIndex
                            ];
                          if (!p) return items[0].label;
                          return [p.label, p.title].filter(Boolean).join(" · ");
                        },
                        label: (item) => {
                          const p =
                            stats.charts.persona_distribution.personas?.[
                              item.dataIndex
                            ];
                          if (!p) return `평균 달성률 ${item.parsed.x}%`;
                          const lines = [
                            `평균 달성률 ${p.avg_percent}%`,
                            `평가 ${p.count}회`,
                          ];
                          if (p.age) lines.push(`${p.age}세`);
                          if (p.purpose_of_visit) {
                            lines.push(String(p.purpose_of_visit).slice(0, 40));
                          }
                          return lines;
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      display: true,
                      max: 100,
                      title: {
                        display: true,
                        // Achievement %, not raw points: score_obtained is out of each
                        // group's own maximum (95 for ACE 2026), so plotting it against a
                        // 0-100 axis labelled 평균 점수 read ~4 points low.
                        text: "평균 달성률 (%)",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                    y: { grid: { display: false } },
                  },
                }}
              />
              )}
            </div>
          </div>
        </Grid.Column>
      </Grid>

      {/* Section 4: Leaderboards. One line per trainee on phones too (a Semantic table
          stacked every cell into its own line); the row opens the trainee's dashboard. */}
      <Header as="h3" style={{ margin: "40px 0 20px", color: "#1d1d1f" }}>
        교육생 현황
      </Header>
      <Grid stackable columns={2}>
        {[
          ["우수 교육생 (Top 5)", "trophy", "#FFD60A", stats.user_stats.top_performers],
          ["집중 케어 필요 (Bottom 5)", "medkit", "#FF3B30", stats.user_stats.low_performers],
        ].map(([title, icon, iconColor, users]) => (
          <Grid.Column key={title}>
            <div style={{ backgroundColor: "white", borderRadius: "16px", padding: isNarrow ? "16px" : "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.03)", height: "100%" }}>
              <Header as="h4" style={{ marginBottom: "12px", color: "#1d1d1f", display: "flex", alignItems: "center" }}>
                <Icon name={icon} style={{ color: iconColor, marginRight: "8px" }} />
                {title}
              </Header>
              {users.length === 0 && <div style={{ color: "#86868b", fontSize: "13px", padding: "12px 0" }}>선택한 기간에 평가된 교육생이 없습니다.</div>}
              {users.map((user, idx) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => navigate(`/userdashboard?userId=${user.id}`)}
                  title="교육생 대시보드 열기"
                  style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "10px 0", border: "none", borderTop: idx ? "1px solid #f5f5f7" : "none", background: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                >
                  <span style={{ width: "16px", color: "#86868b", fontWeight: 700, fontSize: "13px" }}>{idx + 1}</span>
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: "14px", color: "#1d1d1f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</span>
                  <span style={{ flex: `0 0 ${isNarrow ? 56 : 110}px`, height: "6px", borderRadius: "3px", backgroundColor: "#F2F2F7", overflow: "hidden" }}>
                    <span style={{ display: "block", width: `${Math.min(100, user.avg_score)}%`, height: "100%", backgroundColor: scoreColor(user.avg_score) }} />
                  </span>
                  <span style={{ width: "46px", textAlign: "right", fontWeight: 600, fontSize: "13px", color: scoreColor(user.avg_score) }}>{Math.round(user.avg_score)}%</span>
                  <span style={{ width: "34px", textAlign: "right", fontSize: "12px", color: "#86868b" }}>{user.count}회</span>
                  <Icon name="chevron right" style={{ color: "#C7C7CC", margin: 0, fontSize: "11px" }} />
                </button>
              ))}
            </div>
          </Grid.Column>
        ))}
      </Grid>

      {/* Section 5: Criteria Insights */}
      <Grid stackable columns={2} style={{ marginTop: "10px" }}>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header as="h4" style={{ marginBottom: "20px", color: "#34C759" }}>
              <Icon name="check circle" /> 가장 잘 지켜지는 항목
            </Header>
            {stats.criteria_stats.most_met.map((item, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "16px",
                  paddingBottom: "16px",
                  borderBottom: idx < 4 ? "1px solid #f5f5f7" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#1d1d1f",
                      fontSize: "14px",
                      flex: 1,
                      marginRight: "10px",
                      wordBreak: "keep-all",
                    }}
                  >
                    {item.criterion__text}
                  </span>
                  {/* A rate over the times it was evaluated: a raw count mostly said
                      which criteria came up most. */}
                  <span style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    <span style={{ color: "#34C759", fontWeight: "bold" }}>{Math.round(item.met_rate)}%</span>
                    <span style={{ display: "block", fontSize: "12px", color: "#86868b" }}>
                      {item.met}/{item.total}회{item.small_sample ? " · 표본 적음" : ""}
                    </span>
                  </span>
                </div>
                {item.criterion__tags && (
                  <div style={{ fontSize: "12px", color: "#86868b" }}>
                    {item.criterion__tags
                      .split(",")
                      .map((t) => `#${t.trim()} `)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Grid.Column>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header as="h4" style={{ marginBottom: "20px", color: "#FF3B30" }}>
              <Icon name="exclamation circle" /> 가장 많이 놓치는 항목
            </Header>
            {stats.criteria_stats.most_missed.map((item, idx) => (
              // Opens 대화 내역: this period's sessions whose latest evaluation missed it.
              <div
                key={idx}
                role="button"
                title="이 항목을 놓친 대화 보기"
                onClick={() => navigate(`/chatsessionslist?page=1&page_size=${PAGE_SIZE}&missed_criterion=${(item.criterion_ids || []).join(",")}&start_date=${rangeStartDate(range)}`)}
                style={{
                  marginBottom: "16px",
                  paddingBottom: "16px",
                  borderBottom: idx < 4 ? "1px solid #f5f5f7" : "none",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#1d1d1f",
                      fontSize: "14px",
                      flex: 1,
                      marginRight: "10px",
                      wordBreak: "keep-all",
                    }}
                  >
                    {item.criterion__text}
                  </span>
                  {/* A rate over the times it was evaluated: a raw count mostly said
                      which criteria came up most. */}
                  <span style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    <span style={{ color: "#FF3B30", fontWeight: "bold" }}>{Math.round(item.missed_rate)}%</span>
                    <span style={{ display: "block", fontSize: "12px", color: "#86868b" }}>
                      {item.missed}/{item.total}회{item.small_sample ? " · 표본 적음" : ""}
                    </span>
                    <span style={{ display: "block", fontSize: "12px", color: "#007AFF", marginTop: "2px" }}>대화 보기 ›</span>
                  </span>
                </div>
                {item.criterion__tags && (
                  <div style={{ fontSize: "12px", color: "#86868b" }}>
                    {item.criterion__tags
                      .split(",")
                      .map((t) => `#${t.trim()} `)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Grid.Column>
      </Grid>
    </div>
  );

  const ProductKnowledgeTab = () => (
    <div>
      {/* PK Summary Cards */}
      <Grid stackable columns={4} style={{ marginBottom: "20px", marginTop: "20px" }}>
        {[
          // Correct ÷ checked claims pooled across sessions (#283), not a mean of 0–100 scores.
          { label: "설명 정확도", value: `${stats.counts.pk_average_score}%`, icon: "lightbulb", color: "#FFD60A" },
          { label: "퀴즈 풀이 수", value: stats.counts.quiz_attempts, icon: "pencil alternate", color: "#FF3B30" },
          // First answer per trainee×question (what they knew) vs latest (what they know now):
          // the gap is what practice changed. Neither counts repeats (#249).
          { label: "퀴즈 정답률 (첫 시도)", value: `${stats.counts.quiz_accuracy}%`, icon: "check square", color: "#34C759" },
          { label: "퀴즈 정답률 (현재)", value: `${stats.counts.quiz_latest_accuracy ?? stats.counts.quiz_accuracy}%`, icon: "chart line", color: "#007AFF" },
        ].map((item, idx) => (
          <Grid.Column key={idx}>
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '24px', 
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontSize: '13px', color: '#86868b', fontWeight: '500', marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1d1d1f' }}>{item.value}</div>
              </div>
              <div style={{ 
                width: '42px', 
                height: '42px', 
                borderRadius: '10px', 
                backgroundColor: `${item.color}15`, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Icon name={item.icon} style={{ fontSize: '18px', color: item.color, margin: 0 }} />
              </div>
            </div>
          </Grid.Column>
        ))}
      </Grid>

      {/* Both 제품 지식 sources per product, one row each */}
      <ProductKnowledgeByProduct rows={stats.charts.by_product} />

      <Grid stackable columns={1} style={{ marginTop: '20px' }}>
        <Grid.Column>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
            <Header as="h4" style={{ marginBottom: '20px', color: '#1d1d1f' }}>설명 정확도 추이</Header>
            <div style={{ height: '250px' }}>
              <Bar 
                data={{
                  labels: stats.charts.pk_trend.labels,
                  datasets: [{
                    type: 'line',
                    label: '정확도 (%)',
                    data: stats.charts.pk_trend.data,
                    borderColor: '#FFD60A',
                    backgroundColor: 'rgba(255, 214, 10, 0.1)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4,
                    pointRadius: 3
                  }]
                }}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: { 
                        display: true, 
                        min: 0, 
                        max: 100,
                        grid: { color: '#f0f0f0' }
                    }
                  }
                }}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>
    </div>
  );

  // The sections are called, not rendered as <Tab />: as components defined inside StaffHome
  // they were a new type on every render, so each re-render (the 파트너 list arriving, a
  // second stats response) remounted them and every chart replayed its draw-in animation.
  const panes = [
    {
      menuItem: '응대 스킬',
      render: () => <Tab.Pane attached={false} style={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}>{ResponseSkillsTab()}</Tab.Pane>,
    },
    {
      menuItem: '제품 지식',
      render: () => <Tab.Pane attached={false} style={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}>{ProductKnowledgeTab()}</Tab.Pane>,
    },
  ];

  return (
    <div
      style={{
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
        paddingBottom: "60px",
      }}
    >
      {/* Sticky Header */}
      <div
        style={{
          padding: "16px 0",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          marginBottom: "30px",
          position: "sticky",
          top: "50px", // Adjusted to account for fixed Navbar height (approx 50px)
          zIndex: 90, // Lower than Navbar zIndex
        }}
      >
        <Container>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              // The title and the controls used to sit on one unwrappable row. The range
              // pills (~206px) plus the 파트너 dropdown (180px) alone exceed a phone's
              // width, so the row pushed the document to 456px against a 390px viewport —
              // which is a horizontal scroll, and on a pinch-zoomed phone it is what makes
              // the whole page jump. Wrapping costs a line of height and nothing else.
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {/* minWidth:0 so a long 파트너 or user name shrinks instead of forcing the row
                wider than its parent — flex items refuse to shrink below content width
                otherwise, which is how overflow survives a flexWrap. */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
              <div
                style={{
                  width: "40px",
                  flexShrink: 0, // stays a circle when the text beside it wraps
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#007AFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                <Icon
                  name="chart pie"
                  style={{
                    margin: 0,
                    color: "#fff",
                    fontSize: "18px",
                    height: "18px",
                    width: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: "17px",
                    fontWeight: "600",
                    margin: 0,
                    color: "#1d1d1f",
                  }}
                >
                  관리자 대시보드
                </h2>
                <p style={{ fontSize: "13px", color: "#86868b", margin: 0 }}>
                  {user?.full_name || user?.username}님, 환영합니다.
                </p>
              </div>
            </div>
            {/* Below 768px the controls take the whole width and become a proper
                segmented control — the pills divide the row evenly and the dropdown sits
                full-width beneath. They cannot share a line at that size (206px + 180px
                against ~362px of container), so rather than let them wrap raggedly they
                stretch, which reads as a deliberate mobile layout instead of a spill.
                Desktop is untouched: natural widths, both on the title's row. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                width: isNarrow ? "100%" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#F5F5F7",
                  borderRadius: "12px",
                  padding: "3px",
                  height: "40px",
                  width: isNarrow ? "100%" : "auto",
                }}
              >
                {RANGE_OPTIONS.map((opt) => {
                  const isActive = range === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleRangeChange(opt.value)}
                      style={{
                        border: "none",
                        outline: "none",
                        cursor: "pointer",
                        height: "34px",
                        // flex:1 only on mobile, so the four segments divide the row
                        // evenly; on desktop they keep their natural label widths.
                        flex: isNarrow ? 1 : "0 0 auto",
                        padding: isNarrow ? "0 4px" : "0 14px",
                        borderRadius: "9px",
                        fontSize: "13px",
                        fontFamily: "inherit",
                        fontWeight: isActive ? "600" : "500",
                        color: isActive ? "#1d1d1f" : "#86868b",
                        backgroundColor: isActive ? "#ffffff" : "transparent",
                        boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                        transition: "background-color 0.2s, color 0.2s",
                      }}
                    >
                      {opt.text}
                    </button>
                  );
                })}
              </div>
              {user.is_staff ? (
                <CustomDropdown
                  placeholder="전체 파트너"
                  options={contractors}
                  value={selectedContractor}
                  onChange={(value) => setSelectedContractor(value)}
                  style={{ width: isNarrow ? "100%" : "180px", height: "40px" }}
                />
              ) : (
                <span style={{ fontSize: "14px", fontWeight: "600", color: "#1d1d1f" }}>
                  {user.contractor?.name}
                </span>
              )}
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {/* The spinner is for the first load only. A range or 파트너 change keeps the page and
            dims it, instead of tearing everything down (charts, open tab) and rebuilding. */}
        {loading && !stats ? (
          <div
            style={{
              height: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader active inline="centered" size="large">
              데이터 분석 중...
            </Loader>
          </div>
        ) : stats && (
          <div style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.15s", pointerEvents: loading ? "none" : "auto" }}>
            {/* 1. Global Summary */}
            <Header as="h3" style={{ margin: "0 0 20px", color: "#1d1d1f" }}>
              전체 현황
            </Header>
            {SummarySection()}

            {/* 2. Tabbed Detail View */}
            <Tab 
                menu={{ secondary: true, pointing: true }} 
                panes={panes} 
                style={{ marginTop: '30px' }}
            />

            {/* Section 6: 파트너 현황 — every partner in scope, those with no evaluation too:
                the participation funnel (가입 → 승인 → 참여 → 평가 완료, the last two out of the
                approved, in the selected period) and each measure. A row opens 직원 관리 for
                that partner. Cards on phones (ListTable). */}
            {stats.contractor_stats && stats.contractor_stats.length > 0 && (
              <div style={{ marginTop: "60px" }}>
                <Header as="h3" style={{ marginBottom: "20px", color: "#1d1d1f" }}>
                  파트너 현황
                </Header>
                <ListTable
                  isMobile={isNarrow}
                  rows={stats.contractor_stats}
                  onRowClick={(c) => navigate(`/userslist?page=1&page_size=${PAGE_SIZE}&contractor=${c.id}`)}
                  columns={[
                    { key: "name", label: "파트너", nowrap: true, render: (c) => <span style={{ fontWeight: 600 }}>{c.name}</span> },
                    { key: "signed", label: "가입", align: "right", render: (c) => c.signed_up },
                    { key: "approved", label: "승인", align: "right", render: (c) => approvedCell(c) },
                    { key: "started", label: "참여", align: "right", render: (c) => funnelCell(c.started, c.approved) },
                    { key: "evaluated", label: "평가 완료", align: "right", render: (c) => funnelCell(c.evaluated, c.approved) },
                    { key: "score", label: "응대 달성률", nowrap: true, render: (c) => achievementCell(c) },
                    { key: "pass", label: "Pass 비율", align: "right", render: (c) => (c.pass_rate == null ? <span style={{ color: "#86868b" }}>—</span> : `${Math.round(c.pass_rate)}%`) },
                    { key: "pk", label: "설명 정확도", nowrap: true, render: (c) => <RatioScore num={c.pk?.correct} den={c.pk?.checked} /> },
                    { key: "quiz", label: "퀴즈 정답률", nowrap: true, render: (c) => <RatioScore num={c.quiz?.correct} den={c.quiz?.checked} /> },
                  ]}
                  card={{
                    title: (c) => c.name,
                    tags: (c) => [c.approved < c.signed_up && <Tag key="w" tone="amber">승인 대기 {c.signed_up - c.approved}</Tag>],
                    meta: (c) => [`가입 ${c.signed_up}`, `승인 ${c.approved}`, `참여 ${c.started}`, `평가 완료 ${c.evaluated}`],
                    body: (c) => (
                      <span>
                        응대 {achievementCell(c)} · 설명 <RatioScore num={c.pk?.correct} den={c.pk?.checked} /> · 퀴즈 <RatioScore num={c.quiz?.correct} den={c.quiz?.checked} />
                      </span>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Container>
    </div>
  );
};

export default StaffHome;

